import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  TextField,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useSnackbar } from "notistack";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import authServices from "app/services/auth-services";
import SubscriptionCard, { annualSavingsPercent } from "./components/SubscriptionCard";
import {
  useChangeSubscriptionPlan,
  useResolveSubscriptionChangeRequest,
  useSubscriptionChangeRequests,
  useSubscriptionsClientsOverview,
  useSubscriptionsOverview,
  useUpdateMembership,
} from "./hooks/useSubscriptions";

const formatDate = (value) => {
  if (!value) return "Sin fecha de expiracion";
  return new Date(value).toLocaleDateString("es-CL");
};

const formatMetricValue = (metric) => {
  if (!metric?.enabled) return "No incluido";
  if (metric?.unlimited) return `${metric.used} / ∞`;
  if (typeof metric?.limit === "number") return `${metric.used} / ${metric.limit}`;
  return `${metric?.used || 0}`;
};

const metricBarColor = (metric) => {
  if (!metric?.enabled) return "rgba(158, 158, 158, 0.45)";
  if ((metric?.over_by || 0) > 0) return "#d32f2f";
  const percent = Number(metric?.usage_percent || 0);
  if (percent >= 85) return "#ed6c02";
  return "#1976d2";
};

const metricTrackColor = (metric) => {
  if (!metric?.enabled) return "rgba(158, 158, 158, 0.18)";
  if ((metric?.over_by || 0) > 0) return "rgba(211, 47, 47, 0.16)";
  return "rgba(25, 118, 210, 0.12)";
};

const metricProgressValue = (metric) => {
  if (!metric?.enabled) return 0;
  if (metric?.unlimited || typeof metric?.limit !== "number" || metric.limit <= 0) return 100;
  return Math.max(0, Math.min(100, Number(metric?.usage_percent || 0)));
};

function SubscriptionUsageMiniMetric({ metric }) {
  return (
    <Box
      sx={{
        minWidth: 140,
        maxWidth: 160,
        flex: "1 1 140px",
        p: 1,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        {metric?.label || "-"}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={metricProgressValue(metric)}
        sx={{
          height: 7,
          borderRadius: 99,
          mb: 0.5,
          backgroundColor: metricTrackColor(metric),
          "& .MuiLinearProgress-bar": {
            borderRadius: 99,
            backgroundColor: metricBarColor(metric),
          },
        }}
      />
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: (metric?.over_by || 0) > 0 ? "error.main" : "text.primary",
          fontWeight: 600,
        }}
      >
        {formatMetricValue(metric)}
      </Typography>
    </Box>
  );
}

// La fecha va como YYYY-MM-DD en hora local: el backend la toma como el ultimo
// minuto de ese dia en Chile.
const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Solo el superadmin ve la vista de clientes, asi que el lapiz no necesita otro chequeo:
// el backend igual rechaza a cualquier otro perfil.
//
// Cambiar el plan no pisa la membresia: el backend cierra la actual hoy y abre otra con el
// plan y el vencimiento elegidos, asi que el inicio pasa a ser hoy.
function MembershipEditDialog({ row, plans, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const updateMembership = useUpdateMembership();
  const currentPlanSlug = row?.plan?.slug || "";
  const [planSlug, setPlanSlug] = useState(currentPlanSlug);
  const [value, setValue] = useState(() => toDateInputValue(row?.membership?.expires_at));
  const planChanged = planSlug !== currentPlanSlug;
  const minDate = planChanged ? toDateInputValue(new Date()) : toDateInputValue(row?.membership?.started_at);

  const handleSave = () => {
    updateMembership.mutate(
      { membershipId: row.membership.id, expiresAt: value, planSlug: planSlug || undefined },
      {
        onSuccess: () => {
          enqueueSnackbar("Suscripción actualizada", { variant: "success" });
          onClose();
        },
        onError: (error) => {
          enqueueSnackbar(error?.response?.data?.error || "No fue posible actualizar la suscripción", {
            variant: "error",
          });
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Suscripción de {row?.company?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label="Plan"
            value={planSlug}
            onChange={(event) => setPlanSlug(event.target.value)}
            fullWidth
          >
            {(plans || []).map((plan) => (
              <MenuItem key={plan.slug} value={plan.slug}>
                {plan.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            type="date"
            label="Vence"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            inputProps={{ min: minDate || undefined }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <Typography variant="body2" color="text.secondary">
            {planChanged
              ? "El plan nuevo parte hoy y el actual se cierra. La compañía puede usar Haulers hasta el final del día elegido."
              : "La compañía puede usar Haulers hasta el final del día elegido."}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMembership.isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!value || updateMembership.isLoading}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubscriptionsClientsTable({ rows, plans, isLoading, isError, onRetry }) {
  const [editingRow, setEditingRow] = useState(null);

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => onRetry?.()}>
            Reintentar
          </Button>
        }
      >
        No fue posible cargar las suscripciones activas de clientes.
      </Alert>
    );
  }

  if (!rows.length) {
    return <Alert severity="info">No hay suscripciones para mostrar.</Alert>;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 220 }}>Compañía</TableCell>
            <TableCell sx={{ minWidth: 180 }}>Plan actual</TableCell>
            <TableCell sx={{ minWidth: 640 }}>Uso del plan</TableCell>
            <TableCell sx={{ minWidth: 120 }}>Vence</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row?.company?.id} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row?.company?.name || `Compañía #${row?.company?.id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID {row?.company?.id}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row?.plan?.name || "Sin plan"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row?.plan?.slug || row?.company?.subscription_plan_slug || "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {(row?.metrics || []).map((metric) => (
                    <SubscriptionUsageMiniMetric
                      key={`${row?.company?.id}-${metric?.feature_key}`}
                      metric={metric}
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  {formatDate(row?.membership?.expires_at)}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="caption"
                    color={row?.membership?.expired ? "error.main" : "text.secondary"}
                  >
                    {row?.membership?.expired ? "Expirada" : "Activa"}
                  </Typography>
                  {row?.membership?.id && (
                    <Tooltip title="Editar plan y vencimiento">
                      <IconButton size="small" onClick={() => setEditingRow(row)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {editingRow && (
        <MembershipEditDialog
          key={editingRow?.membership?.id}
          row={editingRow}
          plans={plans}
          onClose={() => setEditingRow(null)}
        />
      )}
    </TableContainer>
  );
}

function SubscriptionChangeRequestsSection({
  rows,
  isLoading,
  isError,
  onRetry,
  onResolve,
  resolvingRequestId,
}) {
  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1} mb={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Solicitudes de cambio de plan
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Solicitudes pendientes enviadas por administradores de empresas.
          </Typography>
        </Box>
      </Stack>

      {isLoading && (
        <Box sx={{ textAlign: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}

      {!isLoading && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => onRetry?.()}>
              Reintentar
            </Button>
          }
        >
          No fue posible cargar las solicitudes.
        </Alert>
      )}

      {!isLoading && !isError && !rows.length && (
        <Alert severity="info">No hay solicitudes pendientes.</Alert>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Empresa</TableCell>
                <TableCell>Solicitante</TableCell>
                <TableCell>Plan actual</TableCell>
                <TableCell>Plan solicitado</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {request?.company?.name || `Empresa #${request?.company?.id}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID {request?.company?.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request?.requester?.name || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request?.requester?.email || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{request?.current_plan?.name || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request?.current_plan?.slug || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {request?.requested_plan_slug || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {request?.requested_billing_cycle === "annual" ? "Anual" : "Mensual"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(request?.created_at)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        disabled={resolvingRequestId === request.id}
                        onClick={() => onResolve?.(request, "reject")}
                      >
                        Rechazar
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant="contained"
                        disabled={resolvingRequestId === request.id}
                        onClick={() => onResolve?.(request, "approve")}
                      >
                        Aprobar
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// embedded: se renderiza dentro del tab Suscripciones de Configuracion, donde el
// titulo y el padding de pagina sobran (el tab ya la nombra). Como pagina suelta
// en /subscriptions queda igual que antes.
export default function SubscriptionsIndex({ embedded = false }) {
  const [billingCycle, setBillingCycle] = useState("annual");
  const [moduleView, setModuleView] = useState("subscriptions");
  const [resolvingRequestId, setResolvingRequestId] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const { authToken, authUser, setAuthData } = useJumboAuth();

  // Los permisos del backend salen de user.current_profiles (ver Ability), o sea los
  // perfiles de la empresa activa, y esta pantalla siempre habla de esa empresa
  // (SubscriptionsController resuelve la compania con current_user.current_company).
  // Sin filtrar, quien es customer_admin en otra empresa se llevaba ese permiso al
  // rotar. superadmin va aparte porque es global, igual que is_global_superadmin.
  const currentCompanyId = authUser?.current_company?.id;

  const isSuperadmin = useMemo(
    () => (authUser?.profiles || []).some((profile) => profile?.name === "superadmin"),
    [authUser?.profiles]
  );

  const profileNames = useMemo(
    () =>
      Array.isArray(authUser?.profiles)
        ? authUser.profiles
            .filter((profile) => `${profile?.company_id}` === `${currentCompanyId}`)
            .map((profile) => profile?.name)
            .filter(Boolean)
        : [],
    [authUser?.profiles, currentCompanyId]
  );
  const isSubscriptionAdmin = useMemo(
    () => profileNames.includes("customer_admin") || profileNames.includes("admin"),
    [profileNames]
  );

  const canAccessClientsView = isSuperadmin;

  const activeModuleView = canAccessClientsView ? moduleView : "subscriptions";

  const {
    data: overview,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    refetch: refetchOverview,
  } = useSubscriptionsOverview();

  const {
    data: clientsOverview,
    isLoading: isClientsLoading,
    isError: isClientsError,
    refetch: refetchClients,
  } = useSubscriptionsClientsOverview({
    enabled: activeModuleView === "clients" && canAccessClientsView,
  });

  const {
    data: changeRequestsData,
    isLoading: isChangeRequestsLoading,
    isError: isChangeRequestsError,
    refetch: refetchChangeRequests,
  } = useSubscriptionChangeRequests({
    enabled: isSuperadmin && activeModuleView === "clients",
  });

  const changePlanMutation = useChangeSubscriptionPlan();
  const resolveChangeRequestMutation = useResolveSubscriptionChangeRequest();

  const plans = useMemo(() => overview?.plans || [], [overview]);
  const maxAnnualSavings = useMemo(() => Math.max(0, ...plans.map(annualSavingsPercent)), [plans]);
  const currentMembership = overview?.current_membership || null;
  const currentPlanSlug = currentMembership?.membership_plan?.slug;
  const clientsRows = useMemo(() => clientsOverview?.companies || [], [clientsOverview]);
  const pendingChangeRequests = useMemo(() => changeRequestsData?.requests || [], [changeRequestsData]);

  const handlePlanSelection = async (plan) => {
    const cycle = plan?.type === "paid" ? billingCycle : "monthly";

    try {
      const response = await changePlanMutation.mutateAsync({
        planSlug: plan.slug,
        billingCycle: cycle,
      });

      if (response?.requested) {
        enqueueSnackbar(response?.msg || "Solicitud enviada", { variant: "success" });
        return;
      }

      const refreshedAuthUser = await authServices.getCurrentUser();
      setAuthData({ authToken, authUser: refreshedAuthUser });

      enqueueSnackbar("Suscripcion actualizada correctamente", { variant: "success" });
    } catch (error) {
      if (error?.response?.data?.error) {
        enqueueSnackbar(error.response.data.error, { variant: "error" });
      }
    }
  };

  const handleResolveChangeRequest = async (request, decision) => {
    if (!request?.id) return;

    setResolvingRequestId(request.id);
    try {
      const response = await resolveChangeRequestMutation.mutateAsync({
        id: request.id,
        decision,
      });

      enqueueSnackbar(
        response?.msg || (decision === "approve" ? "Solicitud aprobada" : "Solicitud rechazada"),
        { variant: "success" }
      );
    } catch (error) {
      if (error?.response?.data?.error) {
        enqueueSnackbar(error.response.data.error, { variant: "error" });
      } else {
        enqueueSnackbar("No fue posible resolver la solicitud", { variant: "error" });
      }
    } finally {
      setResolvingRequestId(null);
    }
  };

  const Wrapper = embedded ? Box : Container;
  const wrapperProps = embedded ? {} : { maxWidth: "xl", sx: { py: { xs: 3, md: 5 } } };

  return (
    <Wrapper {...wrapperProps}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mb={embedded ? 3 : 4}
      >
        <Box>
          {!embedded && (
            <Typography id="subs-title" variant="h2" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
              Suscripciones
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary">
            Gestiona tu plan actual y revisa el uso de las suscripciones activas.
          </Typography>
        </Box>

        {canAccessClientsView && (
          <ToggleButtonGroup
            value={activeModuleView}
            exclusive
            size="small"
            onChange={(_event, value) => {
              if (!value) return;
              setModuleView(value);
            }}
          >
            <ToggleButton value="subscriptions">Suscripciones</ToggleButton>
            <ToggleButton value="clients">Clientes</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {activeModuleView === "clients" ? (
        <>
          {isSuperadmin && (
            <SubscriptionChangeRequestsSection
              rows={pendingChangeRequests}
              isLoading={isChangeRequestsLoading}
              isError={isChangeRequestsError}
              onRetry={refetchChangeRequests}
              onResolve={handleResolveChangeRequest}
              resolvingRequestId={resolvingRequestId}
            />
          )}

          <Box sx={{ mb: 1.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Subscripciones por compañía
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Revisa los planes activos y el uso actual de cada empresa.
            </Typography>
          </Box>

          <SubscriptionsClientsTable
            rows={clientsRows}
            plans={clientsOverview?.plans}
            isLoading={isClientsLoading}
            isError={isClientsError}
            onRetry={refetchClients}
          />
        </>
      ) : (
        <>
          <Box mb={4} textAlign="center">
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(event, value) => value && setBillingCycle(value)}
              size="small"
              sx={{ mb: 1 }}
            >
              <ToggleButton value="monthly">Mensual</ToggleButton>
              <ToggleButton value="annual">Anual</ToggleButton>
            </ToggleButtonGroup>
            {maxAnnualSavings > 0 && (
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                Pagando anual ahorras hasta un {maxAnnualSavings}% al mes
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Los límites de entregas y viajes son diarios.
            </Typography>
          </Box>

          {isOverviewError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => refetchOverview()}>
                  Reintentar
                </Button>
              }
              sx={{ mb: 3 }}
            >
              No fue posible cargar las suscripciones.
            </Alert>
          )}

          {currentMembership && (
            <Box
              sx={{
                mb: 4,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                p: { xs: 2, md: 3 },
                backgroundColor: "background.paper",
              }}
            >
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Plan actual
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {currentMembership.membership_plan?.name || "Sin plan"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vence: {formatDate(currentMembership.expires_at)}
                  </Typography>
                </Box>
                <Box textAlign={{ xs: "left", md: "right" }}>
                  <Typography variant="overline" color="text.secondary">
                    Estado
                  </Typography>
                  <Typography variant="h6" color={currentMembership.expired ? "error.main" : "success.main"}>
                    {currentMembership.expired ? "Expirado" : "Activo"}
                  </Typography>
                  {typeof currentMembership.days_remaining === "number" && (
                    <Typography variant="body2" color="text.secondary">
                      {currentMembership.days_remaining} dias restantes
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>
          )}

          {isOverviewLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={plan.slug || plan.id}>
                  <SubscriptionCard
                    plan={plan}
                    billingCycle={billingCycle}
                    isCurrent={currentPlanSlug === plan.slug}
                    isSubmitting={changePlanMutation.isLoading}
                    actionLabel={isSubscriptionAdmin && !isSuperadmin ? "Solicitar cambio" : undefined}
                    onSelect={handlePlanSelection}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Wrapper>
  );
}
